import React, { useState, useEffect } from 'react';
import api, { toArr } from '../api';
import { supabase } from '../supabase';

const C = {
  primary: '#6366f1', secondary: '#10b981', accent: '#f59e0b',
  card: 'rgba(255,255,255,0.05)', cardBorder: 'rgba(99,102,241,0.15)',
  text: '#fff', textMuted: 'rgba(255,255,255,0.5)', textSub: 'rgba(255,255,255,0.7)',
};

const sampleCourses = [
  { id: 1, name: 'Introduction to Generative AI', category: 'GenAI', duration: '45 min', level: 'Beginner', url: null, instructor: 'Dr. Priya Nair', enrolled: 1240 },
  { id: 2, name: 'Prompt Engineering Mastery', category: 'GenAI', duration: '1h 20m', level: 'Intermediate', url: null, instructor: 'Dr. Priya Nair', enrolled: 890 },
  { id: 3, name: 'AWS Cloud Foundations', category: 'Cloud', duration: '2h', level: 'Beginner', url: null, instructor: 'James Wilson', enrolled: 2100 },
  { id: 4, name: 'Kubernetes & Container Orchestration', category: 'DevOps', duration: '1h 45m', level: 'Advanced', url: null, instructor: 'Chen Wei', enrolled: 654 },
  { id: 5, name: 'Machine Learning with Python', category: 'ML', duration: '3h', level: 'Intermediate', url: null, instructor: 'Maria Rodriguez', enrolled: 1780 },
  { id: 6, name: 'Neural Networks Deep Dive', category: 'ML', duration: '2h 30m', level: 'Advanced', url: null, instructor: 'Maria Rodriguez', enrolled: 432 },
  { id: 7, name: 'Cloud Security & Compliance', category: 'Cloud', duration: '1h 30m', level: 'Intermediate', url: null, instructor: 'James Wilson', enrolled: 760 },
  { id: 8, name: 'DevOps & CI/CD Pipelines', category: 'DevOps', duration: '2h 10m', level: 'Intermediate', url: null, instructor: 'Chen Wei', enrolled: 985 },
];

const categoryColors = {
  GenAI: '#6366f1', Cloud: '#10b981', ML: '#f59e0b', DevOps: '#ec4899', Data: '#06b6d4',
};

const levelColors = { Beginner: '#10b981', Intermediate: '#f59e0b', Advanced: '#ef4444' };

const ElitureVideoPlayer = ({ user }) => {
  const [courses, setCourses] = useState(sampleCourses);
  const [activeCategory, setActiveCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [enrolled, setEnrolled] = useState(new Set([1, 3, 5])); // demo: enrolled course IDs
  const [progress, setProgress] = useState({ 1: 72, 3: 45, 5: 88 }); // demo progress
  const [enrollingId, setEnrollingId] = useState(null);
  const [sortBy, setSortBy] = useState('default');

  const isDemoUser = !user?.id || user.id === 'demo-user-id';

  useEffect(() => {
    let cancelled = false;

    const loadFromSupabaseDirect = async () => {
      if (!supabase) return false;
      try {
        const BUCKET = 'videos_blog';
        const VIDEO_RE = /\.(mp4|webm|mov|avi|mkv|m4v)$/i;

        // List root + one level of subfolders
        const { data: rootItems } = await supabase.storage
          .from(BUCKET).list('', { limit: 200, sortBy: { column: 'name', order: 'asc' } });

        const entries = [];
        for (const item of rootItems || []) {
          if (!item.id) {
            // subfolder — list inside
            const { data: subItems } = await supabase.storage
              .from(BUCKET).list(item.name, { limit: 200 });
            for (const sub of subItems || []) {
              if (VIDEO_RE.test(sub.name))
                entries.push({ path: `${item.name}/${sub.name}`, folder: item.name });
            }
          } else if (VIDEO_RE.test(item.name)) {
            entries.push({ path: item.name, folder: null });
          }
        }
        if (!entries.length) return false;

        // Build signed or public URLs
        const list = await Promise.all(
          entries.map(async ({ path, folder }, idx) => {
            // Try signed URL first (works for private buckets)
            const { data: signed } = await supabase.storage
              .from(BUCKET).createSignedUrl(path, 60 * 30);
            // Fall back to public URL
            const { data: pub } = supabase.storage.from(BUCKET).getPublicUrl(path);
            const url = signed?.signedUrl || pub?.publicUrl || null;

            const filename = path.split('/').pop().replace(VIDEO_RE, '');
            const sep = filename.match(/^([A-Za-z]+)[_\- ]/);
            const category = folder
              ? folder.charAt(0).toUpperCase() + folder.slice(1)
              : sep ? sep[1] : 'General';
            const rawTitle = sep ? filename.slice(sep[0].length) : filename;
            const title = rawTitle.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();

            return { id: idx + 1, name: title, title, category, url, key: path, level: 'Intermediate' };
          })
        );

        if (!cancelled && list.length) { setCourses(list); return true; }
        return false;
      } catch { return false; }
    };

    // 1️⃣ Try backend route (signed URLs via supabaseAdmin)
    api.get('/videos/supabase-links')
      .then(res => {
        const list = toArr(res.data);
        if (list.length && !cancelled) { setCourses(list); return; }
        // 2️⃣ Fall back: direct Supabase Storage from browser
        return loadFromSupabaseDirect();
      })
      .catch(async () => {
        // 2️⃣ Backend unreachable — try direct Supabase client
        const loaded = await loadFromSupabaseDirect();
        // 3️⃣ Last resort: keep sampleCourses (already in state)
        if (!loaded && !cancelled) setCourses(sampleCourses);
      });

    return () => { cancelled = true; };
  }, []);

  const handleEnroll = async (e, courseId) => {
    e.stopPropagation();
    if (enrolled.has(courseId)) return;
    setEnrollingId(courseId);
    if (!isDemoUser) {
      await api.post('/users/' + user.id + '/enroll', { courseId }).catch(() => {});
    } else {
      await new Promise(r => setTimeout(r, 500));
    }
    setEnrolled(prev => new Set([...prev, courseId]));
    setProgress(prev => ({ ...prev, [courseId]: 0 }));
    setEnrollingId(null);
  };

  const handleMarkComplete = (courseId) => {
    setProgress(prev => ({ ...prev, [courseId]: 100 }));
  };

  // Guard: ensure courses is always an array before calling array methods
  const safeCourses = Array.isArray(courses) ? courses : [];

  const categories = ['All', ...new Set(safeCourses.map(c => c.category).filter(Boolean))];

  let filtered = safeCourses.filter(c => {
    const matchCat = activeCategory === 'All' || c.category === activeCategory;
    const matchSearch = (c.name || c.title || '').toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  if (sortBy === 'enrolled') filtered = [...filtered].sort((a, b) => (b.enrolled || 0) - (a.enrolled || 0));
  else if (sortBy === 'level') {
    const order = { Beginner: 0, Intermediate: 1, Advanced: 2 };
    filtered = [...filtered].sort((a, b) => (order[a.level] || 0) - (order[b.level] || 0));
  }

  const enrolledCourses = filtered.filter(c => enrolled.has(c.id));
  const notEnrolled = filtered.filter(c => !enrolled.has(c.id));
  const displayCourses = enrolledCourses.length > 0 || notEnrolled.length > 0
    ? [...enrolledCourses, ...notEnrolled]
    : filtered;

  return (
    <div>
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: '1.3rem', fontWeight: 800, color: C.text }}>Training Library</div>
        <div style={{ fontSize: '0.8rem', color: C.textMuted, marginTop: 4 }}>
          {safeCourses.length} courses available · {enrolled.size} enrolled
        </div>
      </div>

      {/* Search, Filter & Sort */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={search} onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search courses..."
          style={{ flex: 1, minWidth: 200, background: 'rgba(255,255,255,0.06)',
            border: '1px solid rgba(99,102,241,0.2)', borderRadius: 10,
            padding: '10px 16px', color: '#fff', fontSize: '0.875rem', outline: 'none' }}
        />
        <select value={sortBy} onChange={e => setSortBy(e.target.value)}
          style={{ background: 'rgba(30,27,60,0.9)', border: '1px solid rgba(99,102,241,0.2)',
            borderRadius: 10, padding: '10px 14px', color: '#fff', fontSize: '0.8rem', outline: 'none', cursor: 'pointer' }}>
          <option value="default">Sort: Default</option>
          <option value="enrolled">Sort: Most Popular</option>
          <option value="level">Sort: By Level</option>
        </select>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)} style={{
              padding: '9px 16px', borderRadius: 10, fontSize: '0.8rem', fontWeight: 600,
              cursor: 'pointer', border: 'none', transition: 'all 0.2s',
              background: activeCategory === cat ? C.primary : 'rgba(255,255,255,0.06)',
              color: activeCategory === cat ? '#fff' : C.textMuted,
              boxShadow: activeCategory === cat ? `0 4px 12px rgba(99,102,241,0.3)` : 'none',
            }}>{cat}</button>
          ))}
        </div>
      </div>

      {/* My Enrolled Courses Banner */}
      {enrolled.size > 0 && enrolledCourses.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: '0.72rem', color: C.secondary, fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>
            ● Enrolled ({enrolledCourses.length})
          </div>
        </div>
      )}

      {/* Video Player Modal */}
      {selectedCourse && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)',
          backdropFilter: 'blur(12px)', zIndex: 200,
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
        }} onClick={() => setSelectedCourse(null)}>
          <div style={{ background: '#1a1730', borderRadius: 20, width: '100%', maxWidth: 840,
            border: '1px solid rgba(99,102,241,0.25)', overflow: 'hidden', maxHeight: '90vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}>

            {/* Video */}
            <div style={{ background: '#000', aspectRatio: '16/9', display: 'flex',
              alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
              {selectedCourse.url ? (
                <video src={selectedCourse.url} controls style={{ width: '100%', height: '100%' }} />
              ) : (
                <div style={{ textAlign: 'center', color: 'rgba(255,255,255,0.5)' }}>
                  <div style={{ fontSize: 64, marginBottom: 12 }}>▶</div>
                  <div style={{ fontSize: '0.9rem' }}>Video content loads from your configured S3 source</div>
                </div>
              )}
            </div>

            {/* Course Info */}
            <div style={{ padding: '24px 28px' }}>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.65rem', fontWeight: 700,
                  color: categoryColors[selectedCourse.category] || C.primary,
                  background: `${categoryColors[selectedCourse.category] || C.primary}18`,
                  padding: '3px 10px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {selectedCourse.category}
                </span>
                <span style={{ fontSize: '0.65rem', fontWeight: 700,
                  color: levelColors[selectedCourse.level] || C.textMuted,
                  background: `${levelColors[selectedCourse.level] || '#666'}18`,
                  padding: '3px 10px', borderRadius: 20 }}>
                  {selectedCourse.level}
                </span>
              </div>
              <div style={{ fontWeight: 800, color: '#fff', fontSize: '1.15rem', marginBottom: 6 }}>
                {selectedCourse.name || selectedCourse.title}
              </div>
              {selectedCourse.instructor && (
                <div style={{ fontSize: '0.8rem', color: C.textMuted, marginBottom: 12 }}>
                  by {selectedCourse.instructor}
                </div>
              )}
              {selectedCourse.description && (
                <div style={{ fontSize: '0.875rem', color: C.textSub, marginBottom: 16, lineHeight: 1.6 }}>
                  {selectedCourse.description}
                </div>
              )}

              {/* Progress */}
              {enrolled.has(selectedCourse.id) && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: '0.8rem', color: C.textMuted }}>Your Progress</span>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: C.text }}>{progress[selectedCourse.id] || 0}%</span>
                  </div>
                  <div style={{ height: 6, background: 'rgba(255,255,255,0.08)', borderRadius: 100, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${progress[selectedCourse.id] || 0}%`,
                      background: 'linear-gradient(90deg, #6366f1, #10b981)', borderRadius: 100 }} />
                  </div>
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  {selectedCourse.duration && (
                    <span style={{ fontSize: '0.8rem', color: C.textMuted }}>⏱ {selectedCourse.duration}</span>
                  )}
                  {selectedCourse.enrolled && (
                    <span style={{ fontSize: '0.8rem', color: C.textMuted }}>👥 {selectedCourse.enrolled.toLocaleString()} enrolled</span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {enrolled.has(selectedCourse.id) && progress[selectedCourse.id] < 100 && (
                    <button onClick={() => handleMarkComplete(selectedCourse.id)} style={{
                      background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                      borderRadius: 8, padding: '8px 16px', color: '#10b981',
                      fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                    }}>✓ Mark Complete</button>
                  )}
                  {!enrolled.has(selectedCourse.id) && (
                    <button onClick={e => { handleEnroll(e, selectedCourse.id); }}
                      disabled={enrollingId === selectedCourse.id} style={{
                        background: 'linear-gradient(135deg, #6366f1, #10b981)',
                        border: 'none', borderRadius: 8, padding: '8px 20px', color: '#fff',
                        fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
                      }}>
                      {enrollingId === selectedCourse.id ? 'Enrolling...' : 'Enroll Now'}
                    </button>
                  )}
                  <button onClick={() => setSelectedCourse(null)} style={{
                    background: 'rgba(255,255,255,0.08)', border: 'none',
                    borderRadius: 8, padding: '8px 18px', color: '#fff', cursor: 'pointer', fontSize: '0.8rem',
                  }}>Close</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Course Grid */}
      {displayCourses.length === 0 ? (
        <div style={{ textAlign: 'center', color: C.textMuted, padding: 60 }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
          No courses found matching your search.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
          {displayCourses.map((course, i) => {
            const catColor = categoryColors[course.category] || C.primary;
            const isEnrolled = enrolled.has(course.id);
            const courseProgress = progress[course.id] || 0;
            const isComplete = courseProgress === 100;

            return (
              <div key={course.id || i} style={{
                background: C.card, border: `1px solid ${isEnrolled ? catColor + '40' : C.cardBorder}`,
                borderRadius: 14, overflow: 'hidden', transition: 'all 0.25s',
                cursor: 'pointer', position: 'relative',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.borderColor = catColor; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = isEnrolled ? catColor + '40' : C.cardBorder; }}
                onClick={() => setSelectedCourse(course)}
              >
                {/* Enrolled badge */}
                {isEnrolled && (
                  <div style={{ position: 'absolute', top: 10, right: 10, zIndex: 2,
                    background: isComplete ? 'rgba(16,185,129,0.9)' : 'rgba(99,102,241,0.85)',
                    borderRadius: 20, padding: '3px 10px', fontSize: '0.65rem', fontWeight: 700, color: '#fff' }}>
                    {isComplete ? '✓ Completed' : 'Enrolled'}
                  </div>
                )}

                {/* Thumbnail */}
                <div style={{ aspectRatio: '16/9', background: `linear-gradient(135deg, ${catColor}30, rgba(0,0,0,0.5))`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
                  <div style={{ width: 48, height: 48, background: `${catColor}40`,
                    borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 22, border: `2px solid ${catColor}60` }}>▶</div>
                  {course.duration && (
                    <div style={{ position: 'absolute', bottom: 8, right: 10,
                      background: 'rgba(0,0,0,0.7)', borderRadius: 6,
                      padding: '2px 8px', fontSize: '0.72rem', color: '#fff', fontWeight: 600 }}>
                      {course.duration}
                    </div>
                  )}
                </div>

                {/* Progress bar (enrolled) */}
                {isEnrolled && (
                  <div style={{ height: 3, background: 'rgba(255,255,255,0.08)' }}>
                    <div style={{ height: '100%', width: `${courseProgress}%`,
                      background: isComplete ? '#10b981' : catColor, transition: 'width 0.5s' }} />
                  </div>
                )}

                <div style={{ padding: '14px 16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: '0.65rem', fontWeight: 700, color: catColor,
                      background: `${catColor}18`, padding: '2px 8px', borderRadius: 20,
                      textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {course.category}
                    </span>
                    <span style={{ fontSize: '0.65rem', fontWeight: 600,
                      color: levelColors[course.level] || C.textMuted }}>
                      {course.level}
                    </span>
                  </div>
                  <div style={{ fontWeight: 700, color: C.text, fontSize: '0.9rem', marginBottom: 6, lineHeight: 1.4 }}>
                    {course.name || course.title}
                  </div>
                  {course.instructor && (
                    <div style={{ fontSize: '0.75rem', color: C.textMuted, marginBottom: 8 }}>
                      by {course.instructor}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    {course.enrolled && (
                      <span style={{ fontSize: '0.72rem', color: C.textMuted }}>
                        👥 {course.enrolled >= 1000 ? `${(course.enrolled / 1000).toFixed(1)}k` : course.enrolled}
                      </span>
                    )}
                    {isEnrolled ? (
                      <span style={{ fontSize: '0.72rem', color: catColor, fontWeight: 600 }}>
                        {courseProgress}% complete
                      </span>
                    ) : (
                      <button onClick={e => handleEnroll(e, course.id)}
                        disabled={enrollingId === course.id} style={{
                          background: 'rgba(99,102,241,0.15)', border: '1px solid rgba(99,102,241,0.25)',
                          borderRadius: 8, padding: '5px 12px', color: C.primary, fontSize: '0.75rem',
                          fontWeight: 600, cursor: 'pointer',
                        }}>
                        {enrollingId === course.id ? '...' : '+ Enroll'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ElitureVideoPlayer;
